import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  Button,
  TextField,
  MenuItem,
  Select,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { get, post, put, remove } from 'api';
import { useTranslation } from 'react-i18next';
import { playoffProgression } from 'utils/tournamentHelper';

const EditMatchResultModal = ({
  open,
  onClose,
  match,
  fetchGroups,
  fetchMainTable,
  successToastRef,
  errorToastRef,
  openedFrom,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [newResultTeam1, setNewResultTeam1] = useState('');
  const [newResultTeam2, setNewResultTeam2] = useState('');
  const [winner, setWinner] = useState('');
  const [loser, setLoser] = useState('');
  const [matchInfo, setMatchInfo] = useState(null);

  useEffect(() => {
    if (open) {
      setMatchInfo(match);
    }
  }, [match, open]);

  useEffect(() => {
    const dataSource = match;

    if (dataSource) {
      setNewResultTeam1(dataSource.resultFromA || '');
      setNewResultTeam2(dataSource.resultFromB || '');
      setWinner(dataSource.winner_id || '');
      setLoser(dataSource.loser_id || '');
    }
  }, [match]);

  const handleResultChange = async () => {
    const dataSource = matchInfo || match;
    if (!dataSource) return;

    const {
      match_id,
      match_number,
      matchKey: match_key,
      winner_id: originalWinner,
      tournament_stage,
      tournament_id,
      resultFromA: existingScore1,
      resultFromB: existingScore2,
      tournament_gender,
      tournament_level,
    } = dataSource;

    try {
      // 1. DELETE
      if (newResultTeam1.trim() === '' && newResultTeam2.trim() === '') {
        const deleteBody = {
          tournament_id,
          tournament_gender,
          tournament_level,
          tournament_stage,
          match_key,
          playerA_id: dataSource.playerA.user_id,
          playerB_id: dataSource.playerB.user_id,
        };

        const response = await put(`/matches/delete-result`, deleteBody);
        const result = await response.json();
        if (response.ok) {
          successToastRef.current.showSuccess(
            result.msg || 'Rezultatas ištrintas',
          );
          openedFrom === 'playoffs' ? fetchMainTable() : fetchGroups();
        } else {
          errorToastRef.current.showError(
            result.msg || 'Klaida trinant rezultatą',
          );
        }
        return;
      }

      // 2. SET FLAGS
      const isSimpleInsert =
        (newResultTeam1 === '1:0' && newResultTeam2 === '0:1') ||
        (newResultTeam1 === '0:1' && newResultTeam2 === '1:0') ||
        (newResultTeam1 === 'B/Ž' && newResultTeam2 === 'B/Ž');

      // Check if existing scores are actual results (like "6:4") vs planned dates/times
      // Actual scores have pattern like "6:4" or "6:4 7:5", planned info has dates/parentheses
      const isActualScore = (str) => {
        if (!str) return false;
        // If it contains parentheses or looks like a date, it's planned info
        if (str.includes('(') || str.includes('-') || str.length > 20) return false;
        // If it has digits followed by colon and digits (score pattern), it's actual
        return /\d+:\d+/.test(str);
      };
      const hasActualScore1 = isActualScore(existingScore1);
      const hasActualScore2 = isActualScore(existingScore2);
      const shouldInsert = !hasActualScore1 && !hasActualScore2;
      const canChangeWinner = true; // Allow winner changes for all stages when editing
      const shouldForward =
        tournament_stage !== 'Grupės' &&
        (shouldInsert || originalWinner !== winner); // Forward on new insert OR winner change

      // 3. BUILD PAYLOAD
      const basePayload = {
        match_id,
        match_key,
        tournament_id,
        match_number,
        ch_set1: isSimpleInsert
          ? newResultTeam1 === 'B/Ž'
            ? 'B/Ž'
            : newResultTeam1 === '1:0'
            ? 1
            : 0
          : null,
        op_set1: isSimpleInsert
          ? newResultTeam2 === 'B/Ž'
            ? 'B/Ž'
            : newResultTeam2 === '1:0'
            ? 1
            : 0
          : null,
        ch_set2: null,
        ch_set3: null,
        op_set2: null,
        op_set3: null,
        ch_tb1: null,
        ch_tb2: null,
        ch_tb3: null,
        op_tb1: null,
        op_tb2: null,
        op_tb3: null,
        score_team1: newResultTeam1,
        score_team2: newResultTeam2,
        match_winner: newResultTeam1 === 'B/Ž' ? 0 : winner,
        match_loser: newResultTeam2 === 'B/Ž' ? 0 : loser,
        tournament_gender,
        tournament_level,
        tournament_stage,
        playerA_id: dataSource.playerA.user_id,
        playerB_id: dataSource.playerB.user_id,
        original_winner: originalWinner,
        original_loser:
          originalWinner === winner
            ? loser
            : originalWinner === loser
            ? winner
            : '',
      };

      // 4. Parse full set data if needed
      if (!isSimpleInsert) {
        const newSetsTeam1 = newResultTeam1.split(' ');
        newSetsTeam1.forEach((set, index) => {
          const regex = /(\d+)?(?:\((\d+)\))?:\s*(\d+)?(?:\((\d+)\))?/;
          const match = regex.exec(set);
          if (match) {
            const [, team1Score, team1TB, team2Score, team2TB] = match.map(
              (val) => (val !== undefined ? parseInt(val, 10) : null),
            );
            basePayload[`ch_set${index + 1}`] = team1Score;
            basePayload[`op_set${index + 1}`] = team2Score;
            basePayload[`ch_tb${index + 1}`] = team1TB;
            basePayload[`op_tb${index + 1}`] = team2TB;
          }
        });
      }

      // 5. Add forwarding logic if needed
      if (shouldForward) {
        const nextKey = playoffProgression[match_key];
        if (nextKey) {
          basePayload.next_match_column = `${nextKey}_rez`;
          basePayload.next_group_column = `${nextKey}_group`;
        }
      }

      // 6. POST
      const response = await post(
        shouldInsert ? `/matches/insert-result` : `/matches/update-result`,
        basePayload,
      );
      const result = await response.json();

      if (response.ok) {
        successToastRef.current.showSuccess(
          result.msg ||
            (shouldInsert ? 'Rezultatas įvestas' : 'Rezultatas atnaujintas'),
        );
        openedFrom === 'playoffs' ? fetchMainTable() : fetchGroups();
      } else {
        errorToastRef.current.showError(result.msg || 'Klaida rezultato įraše');
      }
    } catch (error) {
      console.error('Error handling result:', error);
      errorToastRef.current.showError('Klaida apdorojant rezultatą');
    }
  };

  const handleSave = async () => {
    setLoser(
      winner === match.playerA.user_id
        ? match.playerB.user_id
        : match.playerA.user_id,
    );
    await handleResultChange();
    onClose();
  };

  const dataSource = openedFrom === 'playoffs' ? matchInfo : match;

  if (!dataSource) {
    return null; // Render nothing if no data source is available
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '500px' },
          bgcolor: 'background.paper',
          border: `2px solid ${theme.palette.grey[300]}`,
          boxShadow: 24,
          p: { xs: 2, sm: 4 },
          borderRadius: '8px',
        }}
      >
        <Typography variant="h6" mb={2}>
          Redaguoti Rezultatą
        </Typography>

        {/* Team 1 Details */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {dataSource.playerA.first_name} {dataSource.playerA.last_name}
          </Typography>
          <TextField
            label="1 Žaidėjo Rezultatas"
            fullWidth
            value={newResultTeam1}
            onChange={(e) => setNewResultTeam1(e.target.value)}
            sx={{ mt: 1 }}
          />
        </Box>

        {/* Team 2 Details */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {dataSource.playerB.first_name} {dataSource.playerB.last_name}
          </Typography>
          <TextField
            label="2 Žaidėjo Rezultatas"
            fullWidth
            value={newResultTeam2}
            onChange={(e) => setNewResultTeam2(e.target.value)}
            sx={{ mt: 1 }}
          />
        </Box>

        {/* Winner Selection */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Pasirinkite nugalėtoją
          </Typography>
          <Select
            fullWidth
            value={winner}
            disabled={false}
            onChange={(e) => {
              setWinner(e.target.value);
              setLoser(
                e.target.value === dataSource.playerA.user_id
                  ? dataSource.playerB.user_id
                  : dataSource.playerA.user_id,
              );
            }}
            sx={{
              fontSize: { xs: '14px', sm: '16px' },
              '& .MuiSelect-select': {
                padding: '10px 14px',
              },
            }}
          >
            <MenuItem
              value={dataSource.playerA.user_id}
              sx={{
                fontSize: { xs: '14px', sm: '16px' },
              }}
            >
              {dataSource.playerA.first_name} {dataSource.playerA.last_name}
            </MenuItem>
            <MenuItem
              value={dataSource.playerB.user_id}
              sx={{
                fontSize: { xs: '14px', sm: '16px' },
              }}
            >
              {dataSource.playerB.first_name} {dataSource.playerB.last_name}
            </MenuItem>
          </Select>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={onClose} variant="outlined">
            {t('cancel')}
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {t('save')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditMatchResultModal;
